class ApiProxy {
  constructor() {
    this.proxyUrl = 'https://api.rezcomm.com/authentication/central/prxfortnew_v2';
    this.sessionGenerating = false;
    this.sessionGenerationPromise = null;
    this.sessionValidated = false;
    this.initialized = false;
    this.initializationPromise = null;
    this.currentSessionToken = null;
  }
  
  /**
   * Get session token from HttpOnly cookie for Authorization header
   */
  async getSessionToken() {
    console.log('[APIPROXY DEBUG] getSessionToken() called');
    
    try {
      // If we have a current token from initialization, use it
      if (this.currentSessionToken) {
        console.log('[APIPROXY DEBUG] Using cached session token:', this.currentSessionToken.substring(0, 8) + '...');
        return this.currentSessionToken;
      }
      
      // Read from document.cookie for all environments
      console.log('[APIPROXY DEBUG] Checking cookies...');
      console.log('[APIPROXY DEBUG] All cookies:', document.cookie);
      
      const cookies = document.cookie.split(';');
      const sessionCookie = cookies.find(c => c.trim().startsWith('session_id='));
      
      if (sessionCookie) {
        const token = sessionCookie.split('=')[1].trim();
        console.log('[APIPROXY DEBUG] Found session token in cookie:', token.substring(0, 8) + '...');
        this.currentSessionToken = token; // Cache it
        return token;
      } else {
        console.log('[APIPROXY DEBUG] No session_id cookie found');
      }
      return null;
    } catch (error) {
      console.error('[APIPROXY DEBUG] Session token retrieval error:', error);
      return null;
    }
  }

  /**
   * Initialize session once at startup - GUARANTEED to succeed
   */
  async initializeSession() {
    // If already initialized or in progress, return existing promise
    if (this.initialized) {
      return;
    }
    if (this.initializationPromise) {
      return this.initializationPromise;
    }
    
    // Create initialization promise to prevent race conditions
    this.initializationPromise = this._doInitialization();
    return this.initializationPromise;
  }
  
  async _doInitialization() {
    try {
      // Read existing session token from document.cookie for all environments
      const headers = {
        'Content-Type': 'application/json'
      };
      
      const cookies = document.cookie.split(';');
      const sessionCookie = cookies.find(c => c.trim().startsWith('session_id='));
      if (sessionCookie) {
        const existingToken = sessionCookie.split('=')[1].trim();
        headers['Authorization'] = `Bearer ${existingToken}`;
        console.log('[DEBUG] Sending existing session token for validation:', existingToken.substring(0, 8) + '...');
      }
      
      const response = await fetch(this.proxyUrl, {
        method: 'POST',
        credentials: 'omit',
        headers,
        body: JSON.stringify({
          apiName: 'session_validate_and_refresh',
          requestData: {}
        })
      });
      
      const data = await response.json();
      
      if (data.session_token) {
        // Store token for immediate use
        this.currentSessionToken = data.session_token;
        
        // Set cookie for all environments if session was refreshed
        if (data.refreshed) {
          document.cookie = `session_id=${data.session_token}; Path=/; Max-Age=3600; SameSite=Lax`;
        }
        console.log('[DEBUG] Session initialized:', data.refreshed ? 'NEW' : 'EXISTING', data.session_token.substring(0, 8) + '...');
      }
    } catch (error) {
      console.warn('[WARN] Session initialization had issues but continuing:', error);
    }
    
    // Mark as initialized to prevent future calls
    this.initialized = true;
    this.sessionValidated = true;
  }



  async call(apiKey, requestData = {}) {
    console.log('[APIPROXY DEBUG] ===================');
    console.log('[APIPROXY DEBUG] API Call:', apiKey);
    console.log('[APIPROXY DEBUG] Request Data:', requestData);
    console.log('[APIPROXY DEBUG] Environment:', window.location.hostname);
    console.log('[APIPROXY DEBUG] Current session token:', this.currentSessionToken ? this.currentSessionToken.substring(0, 8) + '...' : 'None');
    console.log('[APIPROXY DEBUG] ===================');
    
    try {
      const payload = {
        apiName: apiKey,
        requestData: requestData
      };
      
      // Special handling for session management APIs (no auth required)
      if (apiKey === 'get_session_token' || apiKey === 'generate_session' || apiKey === 'session_validate_and_refresh') {
        console.log('[APIPROXY DEBUG] Session management API - no auth required');
        
        // For session_validate_and_refresh, send existing token via Authorization header
        const headers = {
          'Content-Type': 'application/json'
        };
        
        if (apiKey === 'session_validate_and_refresh') {
          const cookies = document.cookie.split(';');
          const sessionCookie = cookies.find(c => c.trim().startsWith('session_id='));
          if (sessionCookie) {
            const existingToken = sessionCookie.split('=')[1].trim();
            headers['Authorization'] = `Bearer ${existingToken}`;
            console.log('[APIPROXY DEBUG] Added existing token to headers');
          }
        }
        
        console.log('[APIPROXY DEBUG] Making session API request...');
        const response = await fetch(this.proxyUrl, {
          method: 'POST',
          credentials: 'omit',
          headers,
          body: JSON.stringify(payload)
        });
        
        console.log('[APIPROXY DEBUG] Session API response status:', response.status);
        
        if (!response.ok) {
          console.error('[APIPROXY DEBUG] Session API failed:', response.status, response.statusText);
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const result = await response.json();
        console.log('[APIPROXY DEBUG] Session API result:', result);
        
        // For generate_session, manually set cookie on all environments
        if (apiKey === 'generate_session' && result.session_token) {
          document.cookie = `session_id=${result.session_token}; Path=/; Max-Age=3600; SameSite=Lax`;
          this.currentSessionToken = result.session_token;
          console.log('[APIPROXY DEBUG] Manually set session cookie:', result.session_token.substring(0, 8) + '...');
        }
        
        // Update cached token for any session API that returns a token
        if (result.session_token) {
          this.currentSessionToken = result.session_token;
          console.log('[APIPROXY DEBUG] Updated cached session token from API response');
        }
      }
      
      // Initialize session only once
      if (!this.initialized) {
        console.log('[APIPROXY DEBUG] Initializing session...');
        await this.initializeSession();
        console.log('[APIPROXY DEBUG] Session initialization complete');
      }
      
      // Get session token for Authorization
      console.log('[APIPROXY DEBUG] Getting session token...');
      const sessionToken = await this.getSessionToken();
      console.log('[APIPROXY DEBUG] Session token retrieved:', sessionToken ? sessionToken.substring(0, 8) + '...' : 'None');
      
      if (!sessionToken) {
        console.warn('[APIPROXY DEBUG] No session token available, API call may fail');
      }
      
      // Make API call with Authorization header
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
        console.log('[APIPROXY DEBUG] Added Authorization header');
      }
      
      console.log('[APIPROXY DEBUG] Making API request to:', this.proxyUrl);
      console.log('[APIPROXY DEBUG] Request headers:', headers);
      
      const response = await fetch(this.proxyUrl, {
        method: 'POST',
        credentials: 'omit',
        headers,
        body: JSON.stringify(payload),
      });

      console.log('[APIPROXY DEBUG] API response status:', response.status);
      console.log('[APIPROXY DEBUG] API response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        console.error('[APIPROXY DEBUG] API request failed:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('[APIPROXY DEBUG] Error response body:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[APIPROXY DEBUG] API response data:', result);
      
      // Update cached token if API returns session_token (like OTP verify)
      if (result.session_token) {
        this.currentSessionToken = result.session_token;
        console.log('[APIPROXY DEBUG] Updated cached session token from regular API response');
      }
      
      return result;
    } catch (error) {
      console.error('[APIPROXY DEBUG] Proxy API call failed:', error);
      console.error('[APIPROXY DEBUG] Error details:', {
        message: error.message,
        stack: error.stack
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async get(apiKey, params) {
    return this.call(apiKey, params);
  }

  async post(apiKey, data, params) {
    return this.call(apiKey, { ...data, ...params });
  }

  async put(apiKey, data, params) {
    return this.call(apiKey, { ...data, ...params });
  }

  async delete(apiKey, params) {
    return this.call(apiKey, params);
  }
}

const apiProxy = new ApiProxy();
export { apiProxy };
export default apiProxy;
